<template>
  <v-container fluid>
    <v-row class="mb-8 px-5">
      <v-col xs="0" sm="0" md="0" lg="1" xl="1" />
      <v-col xs="12" sm="12" md="12" lg="10" xl="10" class="py-8">
        <div class="d-flex"><v-col class="text-h3 font-weight-light text-center">{{ $t('menus.operations') }}</v-col></div>
      </v-col>
    </v-row>

    <select-operation-type v-model="selectTypeModal" @operation-details-submitted="handleOperationDetailsSubmitted" />
    <upload-files-r-f-q
      v-model="filesUpload"
      :operationList="operationList"
      :id="operationId"
      :operationDetails="operationDetails"
      @operationInfo="handleOperationName"
    />
    <delete-modal
      v-model="deleteDialog"
      :loading="deleteLoading"
      :item-label="operationToDelete ? operationToDelete.alias : ''"
      @confirm="confirmDeleteOperation"
    />
    <edit-operation-files-modal
      v-model="editFilesModal"
      :operation="editFilesOperation"
      :initial-files="editFilesInitialFiles"
      @updated="fetchOperationIdList"
    />

    <v-footer color="transparent">
      <v-col cols="12">
        <v-sheet width="100%" class="py-6">
          <v-row no-gutters>
            <v-col xs="0" sm="0" md="0" lg="1" xl="1" />
            <v-col xs="12" sm="12" md="12" lg="10" xl="10" class="pt-4 d-flex flex-column">
              <div class="d-flex flex-row flex-wrap mx-5 justify-space-between align-center flex-gap-2">
                <div class="text-h5 font-weight-light mb-6">{{ $t('menus.registeredOperations') }}</div>
                <div class="d-flex flex-wrap flex-gap-2 mb-6">
                  <v-btn class="mx-0" outlined :loading="downloadLoading" :disabled="enableDownlaodBasedOnCheckBox" @click="downloadOperation">
                    {{ $t('console.tlsSummary.certificates.download') }}
                  </v-btn>
                  <v-btn color="primary" data-testid="new-operation" @click="openUploadModal(null)">{{ $t('instructions.new') }}</v-btn>
                </div>
              </div>

              <operation-r-f-q
                :operation-list="operationList"
                :selected-operations="checkbox"
                :newest-operation-id="newestOperationId"
                @selection-change="$set(checkbox, $event.id, $event.selected)"
                @delete-operation="requestDeleteOperation"
                @operation-updated="fetchOperationIdList"
                @add-files="handleAddFiles"
              />
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>

      <v-snackbar v-model="globalSnackbar.show" :timeout="4000" :color="globalSnackbar.color" bottom>
        <div class="black--text text-center w-100">{{ globalSnackbar.message }}</div>
      </v-snackbar>
    </v-footer>
  </v-container>
</template>

<script>
import { mapStores } from 'pinia'
import { useExecutionStore, useImportFiles } from '@/store/index'
import UploadFilesRFQ from '@/components/modal/UploadFilesRFQ.vue'
import SelectOperationType from '@/components/modal/selectOperationType.vue'
import OperationRFQ from '@/components/operations/OperationRFQ.vue'
import DeleteModal from '@/components/modal/DeleteModal.vue'
import EditOperationFilesModal from '@/components/modal/EditOperationFilesModal.vue'

export default {
  name: 'OperationView',
  components: { UploadFilesRFQ, SelectOperationType, OperationRFQ, DeleteModal, EditOperationFilesModal },
  data () {
    return {
      globalSnackbar: { show: false, message: '', color: 'success' },
      checkbox: {},
      operationList: [],
      alias: '',
      operationId: undefined,
      filesUpload: false,
      selectTypeModal: false,
      operationDetails: {},
      downloadLoading: false,
      deleteDialog: false,
      deleteLoading: false,
      operationToDelete: null,
      statsInterval: null,
      editFilesModal: false,
      editFilesOperation: null,
      editFilesInitialFiles: []
    }
  },
  computed: {
    ...mapStores(useExecutionStore),
    newestOperationId () {
      const unlinkedOperations = this.operationList.filter(operation => !operation.rfqId)
      if (!unlinkedOperations.length) return null
      const toTimestamp = value => {
        if (!value) return 0
        if (typeof value === 'number') return value > 1e12 ? value : value * 1000
        const timestamp = new Date(String(value).replace(' ', 'T')).getTime()
        return Number.isNaN(timestamp) ? 0 : timestamp
      }
      return unlinkedOperations.reduce((newest, operation) =>
        toTimestamp(operation.timecreate) > toTimestamp(newest?.timecreate) ? operation : newest
      ).id
    },
    enableDownlaodBasedOnCheckBox () {
      return Object.values(this.checkbox).every(value => !value)
    }
  },
  mounted () {
    this.executionStore.selectedId = null
    this.fetchOperationIdList()
    this.statsInterval = setInterval(this.fetchOperationIdList, 3000)
  },
  beforeDestroy () {
    clearInterval(this.statsInterval)
  },
  methods: {
    async downloadOperation () {
      const operationIds = Object.keys(this.checkbox).filter(id => this.checkbox[id])
      if (!operationIds.length) return
      this.downloadLoading = true
      const zipStore = useImportFiles()
      try {
        for (const operationId of operationIds) {
          const operation = this.operationList.find(item => item.id === operationId)
          const name = operation?.companyName || operation?.alias || operationId
          await zipStore.downloadFiles('operation', operationId, name, false)
        }
        this.checkbox = {}
      } catch (error) {
        console.log(error)
      } finally {
        this.downloadLoading = false
      }
    },
    fetchOperationIdList () {
      this.executionStore.getOperationIdList()
        .then(response => { this.operationList = response.data?.operations || [] })
        .catch(console.log)
    },
    handleOperationName ({ alias }) {
      this.alias = alias
    },
    showSnackbar (message, color = 'success') {
      this.globalSnackbar = { show: true, message, color }
    },
    requestDeleteOperation (id) {
      this.operationToDelete = this.operationList.find(operation => operation.id === id) || null
      this.deleteDialog = Boolean(this.operationToDelete)
    },
    confirmDeleteOperation () {
      if (!this.operationToDelete) return
      this.deleteLoading = true
      this.executionStore.deleteOperation(this.operationToDelete.id)
        .then(() => {
          this.showSnackbar(this.$t('broker.snackBar.operationDeleteSuccess'))
          this.fetchOperationIdList()
          this.deleteDialog = false
          this.operationToDelete = null
        })
        .catch(error => {
          console.log(error)
          this.showSnackbar(this.$t('broker.snackBar.operationDeleteError'), 'error')
        })
        .finally(() => { this.deleteLoading = false })
    },
    openUploadModal (id) {
      this.operationId = id !== null ? id : String(Math.floor(Math.random() * 1000) + 1)
      this.selectTypeModal = true
    },
    handleOperationDetailsSubmitted (details) {
      this.operationDetails = { ...details }
      this.filesUpload = true
    },
    handleAddFiles ({ operation, files = [] }) {
      this.editFilesOperation = operation
      this.editFilesInitialFiles = files
      this.editFilesModal = true
    }
  }
}
</script>
